import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import CartBannerHeader from "./CartBannerHeader";
import { useUser } from "../../context/UserContext";
import '../../assets/css/theme.css';
import '../../assets/css/cart.css';
import type { CartItem } from "../../types";

const Cart = () => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const { user } = useUser();

  useEffect(() => {
    try {
      const storedCart = JSON.parse(localStorage.getItem("cart") || 'null') || [];
      setCartItems(storedCart);
    } catch (e) {
      setCartItems([]);
    }
  }, []);

  const handleQuantityChange = (id: number, action: "increase" | "decrease", size: string, color: string) => {
    setCartItems((items) => {
      const updated = items.map((item) => {
        if (item.id === id && item.size === size && item.color === color) {
          return {
            ...item,
            quantity:
              action === "increase"
                ? item.quantity + 1
                : Math.max(1, item.quantity - 1),
          };
        }
        return item;
      });
      localStorage.setItem("cart", JSON.stringify(updated));
      window.dispatchEvent(new Event("cartUpdated"));
      return updated;
    });
  };

  const handleRemoveItem = (id: number, size: string, color: string) => {
    setCartItems((items) => {
      const updated = items.filter(
        (item) => !(item.id === id && item.size === size && item.color === color)
      );
      localStorage.setItem("cart", JSON.stringify(updated));
      window.dispatchEvent(new Event("cartUpdated"));
      return updated;
    });
  };

  const getDiscountedPrice = (item: CartItem): number => {
    if (item.discount_percentage && Number(item.discount_percentage) > 0) {
      return Number(item.price) - (Number(item.price) * Number(item.discount_percentage)) / 100;
    }
    return Number(item.price);
  };

  const isVendor = localStorage.getItem("authToken") && user?.created_by;

  const subtotal = cartItems.reduce((sum, item) => {
    const pricePerItem =
      isVendor && Number(item.discount_percentage) > 0
        ? getDiscountedPrice(item)
        : Number(item.price);
    return sum + pricePerItem * item.quantity;
  }, 0);

  const originalTotal = cartItems.reduce(
    (sum, item) => sum + Number(item.price) * item.quantity,
    0
  );

  const totalSavings = originalTotal - subtotal;

  const itemCount = cartItems.reduce((n, item) => n + Number(item.quantity || 0), 0);

  return (
    <main className="ffc-page">
      <CartBannerHeader currentStep={1} />

      <section className="ffc-body">
        <div className="ffc-container">
          {cartItems.length > 0 ? (
            <div className="ffc-layout">
              {/* ── Cart Items ── */}
              <div>
                <div className="ffc-section-head">
                  <h2 className="ffc-section-title">Your bag</h2>
                  <span className="ffc-count">
                    {cartItems.length} {cartItems.length === 1 ? "item" : "items"}
                  </span>
                </div>

                {cartItems.map((item, idx) => {
                  const hasDiscount = isVendor && Number(item.discount_percentage) > 0;
                  const discounted  = getDiscountedPrice(item);

                  return (
                    <div
                      key={item.id + "-" + item.size + "-" + item.color + "-" + idx}
                      className="ffc-item"
                    >
                      <img
                        src={item.image}
                        alt={item.name}
                        className="ffc-item-img"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = require("../../assets/images/product-item-1.jpg");
                        }}
                      />

                      <div className="ffc-item-info">
                        <Link to={`/product/${item.slug || item.id}`} className="ffc-item-name">
                          {item.name}
                        </Link>

                        <div className="ffc-chips">
                          {item.size && <span className="ffc-chip">Size: {item.size}</span>}
                          {item.color && <span className="ffc-chip">Colour: {item.color}</span>}
                        </div>

                        <div className="ffc-item-actions">
                          <div className="ffc-qty">
                            <button
                              type="button"
                              onClick={() => handleQuantityChange(item.id, "decrease", item.size, item.color)}
                              aria-label="Decrease quantity"
                            >
                              <FontAwesomeIcon icon={faMinus} />
                            </button>
                            <span className="ffc-qty-num">{item.quantity}</span>
                            <button
                              type="button"
                              onClick={() => handleQuantityChange(item.id, "increase", item.size, item.color)}
                              aria-label="Increase quantity"
                            >
                              <FontAwesomeIcon icon={faPlus} />
                            </button>
                          </div>

                          <button
                            type="button"
                            className="ffc-remove"
                            onClick={() => handleRemoveItem(item.id, item.size, item.color)}
                            title="Remove item"
                          >
                            <FontAwesomeIcon icon={faTrash} /> Remove
                          </button>
                        </div>
                      </div>

                      <div className="ffc-item-price">
                        {hasDiscount ? (
                          <>
                            <span className="ffc-price">₹{(discounted * item.quantity).toFixed(2)}</span>
                            <span className="ffc-price-old">₹{(Number(item.price) * item.quantity).toFixed(2)}</span>
                          </>
                        ) : (
                          <span className="ffc-price">₹{(Number(item.price) * item.quantity).toFixed(2)}</span>
                        )}
                      </div>
                    </div>
                  );
                })}

                <Link to="/products" className="ffc-link">← Continue shopping</Link>
              </div>

              {/* ── Order Summary Sidebar ── */}
              <aside className="ffc-side">
                <div className="ffc-card">
                  <h3 className="ffc-card-title">Order summary</h3>

                  <div className="ffc-row">
                    <span>Subtotal ({itemCount} {itemCount === 1 ? "item" : "items"})</span>
                    <span>₹{originalTotal.toFixed(2)}</span>
                  </div>

                  {totalSavings > 0 && (
                    <div className="ffc-row ffc-row--discount">
                      <span>Discount</span>
                      <span>− ₹{totalSavings.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="ffc-row">
                    <span>Shipping</span>
                    <span className="ffc-free">FREE</span>
                  </div>

                  <div className="ffc-divider" />

                  <div className="ffc-row ffc-row--total">
                    <span>Total</span>
                    <span>₹{subtotal.toFixed(2)}</span>
                  </div>

                  {totalSavings > 0 && (
                    <div className="ffc-save">You save ₹{totalSavings.toFixed(2)} on this order</div>
                  )}

                  <Link to="/cart/address" className="ffh-btn ffc-btn--block" style={{ marginTop: 24 }}>
                    Proceed to checkout
                  </Link>

                  <ul className="ffc-trust">
                    <li>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></svg>
                      Secure &amp; encrypted payments
                    </li>
                    <li>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" /><circle cx="7.5" cy="17.5" r="1.5" /><circle cx="17.5" cy="17.5" r="1.5" /></svg>
                      Free shipping on every order
                    </li>
                  </ul>
                </div>
              </aside>
            </div>
          ) : (
            <div className="ffc-empty">
              <span className="ffc-empty-icon">
                <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 8h14l-1 12H6L5 8z" /><path d="M9 8V6a3 3 0 016 0v2" />
                </svg>
              </span>
              <h2 className="ffc-empty-title">Your cart is empty</h2>
              <p className="ffc-empty-sub">Discover our curated collections</p>
              <Link to="/products" className="ffh-btn">Continue shopping</Link>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default Cart;